import { motion } from "framer-motion";
import { Plus, Trash, PencilSimple, CalendarCheck, Clock, UserCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import {
  APPOINTMENT_TYPE_LABELS,
  APPOINTMENT_TYPE_COLORS,
} from "@/lib/appointmentTypes";

export default function AppointmentList({
  selectedDate,
  appointments,
  onNew,
  onEdit,
  onDelete,
}) {
  return (
    <div className="bg-[#171717] border border-[#262626] rounded-lg p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3
            className="text-xl font-bold tracking-tight uppercase text-white"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            {format(selectedDate, "EEEE d MMMM yyyy", { locale: it }).toUpperCase()}
          </h3>
          <p className="text-zinc-500 text-xs mt-1">
            {appointments.length} appuntament{appointments.length === 1 ? "o" : "i"}
          </p>
        </div>
        <Button
          onClick={onNew}
          variant="outline"
          size="sm"
          className="border-[#262626] text-zinc-300 hover:bg-[#1A1A1A] hover:text-white"
          data-testid="add-day-appointment"
        >
          <Plus size={16} className="mr-1" /> Aggiungi
        </Button>
      </div>

      {appointments.length > 0 ? (
        <div className="space-y-3">
          {appointments.map((apt, i) => (
            <motion.div
              key={apt.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="flex items-start gap-4 p-4 bg-[#0A0A0A] rounded-lg border border-[#1A1A1A] group"
              data-testid={`appointment-card-${apt.id}`}
            >
              <div
                className="w-1 self-stretch rounded-full flex-shrink-0"
                style={{ backgroundColor: APPOINTMENT_TYPE_COLORS[apt.appointment_type] || "#007AFF" }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                    style={{
                      backgroundColor: `${APPOINTMENT_TYPE_COLORS[apt.appointment_type] || "#007AFF"}20`,
                      color: APPOINTMENT_TYPE_COLORS[apt.appointment_type] || "#007AFF",
                    }}
                  >
                    {APPOINTMENT_TYPE_LABELS[apt.appointment_type] || apt.appointment_type}
                  </span>
                  {apt.time_start && (
                    <span className="flex items-center gap-1 text-xs text-zinc-500">
                      <Clock size={12} />
                      {apt.time_start}
                      {apt.time_end ? ` - ${apt.time_end}` : ""}
                    </span>
                  )}
                </div>
                <p className="text-sm font-medium text-white">{apt.title}</p>
                {apt.client_name && (
                  <p className="flex items-center gap-1 text-xs text-zinc-400 mt-1">
                    <UserCircle size={14} />
                    {apt.client_name}
                  </p>
                )}
                {apt.notes && (
                  <p className="text-xs text-zinc-600 mt-1">{apt.notes}</p>
                )}
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => onEdit(apt)}
                  className="text-zinc-600 hover:text-[#007AFF] transition-colors"
                  data-testid={`edit-appointment-${apt.id}`}
                >
                  <PencilSimple size={16} />
                </button>
                <button
                  onClick={() => onDelete(apt.id)}
                  className="text-zinc-600 hover:text-red-400 transition-colors"
                  data-testid={`delete-appointment-${apt.id}`}
                >
                  <Trash size={16} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <CalendarCheck size={40} weight="duotone" className="text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">Nessun appuntamento per questo giorno</p>
          <button
            onClick={onNew}
            className="mt-2 text-[#007AFF] text-sm hover:text-[#3395FF] transition-colors"
            data-testid="empty-add-appointment"
          >
            Aggiungi un appuntamento
          </button>
        </div>
      )}
    </div>
  );
}
